/**
 * Single source of truth for every backend API path the web app calls.
 *
 * Why centralise?
 *   - One place to update if a route changes (e.g. /churches → /v2/churches)
 *   - Easy to audit which endpoints the UI actually uses
 *   - Helps testers / new contributors discover the surface area
 *
 * Convention:
 *   - Static paths are zero-arg arrow functions so call sites read the same
 *     way for every endpoint (`endpoints.groups()` vs `endpoints.group(id)`).
 *   - Path params are passed as positional arguments.
 *   - Query strings stay in the call site (rare; only `prayersIncludeHidden`).
 */

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fail loud, fail early. Without REACT_APP_BACKEND_URL every API URL becomes
// `undefined/api/...` and every request silently 404s — a notoriously slow
// bug to diagnose. Surface it on module load so the very first render warns.
if (!RAW_BACKEND_URL) {
  console.error(
    "[Prayer Tracker] REACT_APP_BACKEND_URL is unset. " +
      "Add it to frontend/.env (preview) or the deployment Secrets panel " +
      "(production). Every /api/* request will 404 until this is fixed.",
  );
}

export const API_BASE = `${RAW_BACKEND_URL}/api`;

export const endpoints = {
  // ── Auth ────────────────────────────────────────────────────────────────
  authLogin: () => `${API_BASE}/auth/login`,
  authRegister: () => `${API_BASE}/auth/register`,
  authRegisterInvite: () => `${API_BASE}/auth/register-invite`,
  authLogout: () => `${API_BASE}/auth/logout`,
  authProfile: () => `${API_BASE}/auth/profile`,
  authForgotPassword: () => `${API_BASE}/auth/forgot-password`,
  authResetPassword: () => `${API_BASE}/auth/reset-password`,

  // ── Prayers ─────────────────────────────────────────────────────────────
  prayers: () => `${API_BASE}/prayers`,
  prayersIncludeHidden: () => `${API_BASE}/prayers?include_hidden=true`,
  prayer: (id) => `${API_BASE}/prayers/${id}`,
  prayerPrayedFor: (id) => `${API_BASE}/prayers/${id}/prayed-for`,
  prayersBulkHide: () => `${API_BASE}/prayers/bulk-hide`,
  prayersBulkAnswer: () => `${API_BASE}/prayers/bulk-answer`,
  prayersBulkDelete: () => `${API_BASE}/prayers/bulk-delete`,
  prayersBulkTags: () => `${API_BASE}/prayers/bulk-tags`,
  stats: () => `${API_BASE}/stats`,
  analytics: () => `${API_BASE}/analytics`,

  // ── Groups ──────────────────────────────────────────────────────────────
  groups: () => `${API_BASE}/groups`,
  group: (id) => `${API_BASE}/groups/${id}`,
  groupMembers: (id) => `${API_BASE}/groups/${id}/members`,
  groupMember: (groupId, memberId) =>
    `${API_BASE}/groups/${groupId}/members/${memberId}`,
  groupMemberRole: (groupId, memberId) =>
    `${API_BASE}/groups/${groupId}/members/${memberId}/role`,
  groupPrayers: (id) => `${API_BASE}/groups/${id}/prayers`,
  groupAnalytics: (id) => `${API_BASE}/groups/${id}/analytics`,
  groupInviteEmail: (id) => `${API_BASE}/groups/${id}/invite-email`,
  groupJoin: (inviteCode) => `${API_BASE}/groups/join/${inviteCode}`,

  // ── Churches ────────────────────────────────────────────────────────────
  churches: () => `${API_BASE}/churches`,
  churchesPublic: () => `${API_BASE}/churches/public`,
  church: (id) => `${API_BASE}/churches/${id}`,
  churchMembers: (id) => `${API_BASE}/churches/${id}/members`,
  churchGroups: (id) => `${API_BASE}/churches/${id}/groups`,

  // ── Admin ───────────────────────────────────────────────────────────────
  adminStats: () => `${API_BASE}/admin/stats`,
  adminUsers: () => `${API_BASE}/admin/users`,
  adminUser: (id) => `${API_BASE}/admin/users/${id}`,
  adminUserResetPassword: (id) => `${API_BASE}/admin/users/${id}/reset-password`,
  adminUserChurchAdmin: (id) => `${API_BASE}/admin/users/${id}/church-admin`,
  adminGroups: () => `${API_BASE}/admin/groups`,
  adminGroup: (id) => `${API_BASE}/admin/groups/${id}`,
  adminGroupToggleStatus: (id) => `${API_BASE}/admin/groups/${id}/toggle-status`,
  adminGroupOwner: (id) => `${API_BASE}/admin/groups/${id}/owner`,
  adminGroupMoveMembers: (id) => `${API_BASE}/admin/groups/${id}/move-members`,

  // ── Donations ───────────────────────────────────────────────────────────
  donationsCheckout: () => `${API_BASE}/donations/checkout`,
  donationsStatus: (sid) => `${API_BASE}/donations/status/${sid}`,
  donationsHistory: () => `${API_BASE}/donations/history`,
  donationsRecent: () => `${API_BASE}/donations/recent`,

  // ── Notifications ───────────────────────────────────────────────────────
  notifications: () => `${API_BASE}/notifications`,
  notificationsUnreadCount: () => `${API_BASE}/notifications/unread-count`,
  notificationsMarkRead: () => `${API_BASE}/notifications/mark-read`,
};
